/**
 * English / Urdu toggle (task #36, Dukaan cross-check report §3.4).
 *
 * Scoped deliverable, not a full app-wide translation: Dukaan-POS's bilingual
 * UI was flagged because Pakistani shop staff often read Urdu more
 * comfortably than English chrome, and a wrong-language app is a real
 * adoption barrier even when every feature underneath works. Translating
 * every label on every one of Pospak's ~30 modules (hundreds of form
 * fields, table headers, validation messages) is a much larger project than
 * this pass covers. What ships here: a persistent language switch, RTL
 * `dir` on the document for correct Urdu text shaping/alignment, and
 * translated navigation + top-level chrome (sidebar, header, sign-out,
 * common loading/empty states) — the parts of the screen visible on every
 * single page, which is where a first impression is made. Individual
 * feature screens (Sell, Catalog, Reports, …) keep their English labels for
 * now; each can pick up `useT()` incrementally without needing this
 * plumbing rebuilt.
 *
 * `localStorage`, not `sessionStorage` like the auth store — a language
 * preference should survive logging out and back in, unlike a session token.
 */
import { create } from 'zustand';

export type Lang = 'en' | 'ur';

const STORAGE_KEY = 'pospak.lang';

const DICTIONARY: Record<string, string> = {
  // Nav sections
  'nav.section.operations': 'آپریشنز',
  'nav.section.catalog': 'کیٹلاگ',
  'nav.section.finance': 'مالیات',
  'nav.section.people': 'عملہ',
  'nav.section.admin': 'انتظامیہ',
  // Nav items
  'nav.dashboard': 'ڈیش بورڈ',
  'nav.sell': 'فروخت',
  'nav.bills': 'بلز',
  'nav.shift': 'شفٹ',
  'nav.orders': 'آرڈرز',
  'nav.customers': 'گاہک',
  'nav.quotations': 'کوٹیشنز',
  'nav.bulkOrder': 'تھوک آرڈر',
  'nav.products': 'مصنوعات',
  'nav.pricelists': 'قیمت کی فہرستیں',
  'nav.inventory': 'انوینٹری',
  'nav.purchasing': 'خریداری',
  'nav.accounts': 'اکاؤنٹس',
  'nav.mobile': 'موبائل شاپ',
  'nav.fbr': 'ایف بی آر انوائسنگ',
  'nav.reports': 'رپورٹس',
  'nav.hr': 'ایچ آر اور تنخواہ',
  'nav.shops': 'دکانیں',
  'nav.users': 'صارفین',
  'nav.roles': 'کردار',
  'nav.support': 'سپورٹ',
  'nav.settings': 'ترتیبات',
  // Chrome
  'chrome.signOut': 'سائن آؤٹ',
  'chrome.changePassword': 'پاس ورڈ تبدیل کریں',
  'chrome.loading': 'لوڈ ہو رہا ہے…',
};

interface I18nState {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

function readStored(): Lang {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'ur' ? 'ur' : 'en';
  } catch {
    return 'en';
  }
}

function applyDocumentDirection(lang: Lang): void {
  document.documentElement.dir = lang === 'ur' ? 'rtl' : 'ltr';
  document.documentElement.lang = lang === 'ur' ? 'ur' : 'en';
}

export const useI18n = create<I18nState>((set) => {
  const initial = readStored();
  // Run once at module init so the very first paint is already in the right
  // direction — waiting for a React effect would flash LTR-then-RTL.
  if (typeof document !== 'undefined') applyDocumentDirection(initial);

  return {
    lang: initial,
    setLang: (lang) => {
      try {
        localStorage.setItem(STORAGE_KEY, lang);
      } catch {
        // Private-browsing mode — the in-memory choice still works for this tab.
      }
      applyDocumentDirection(lang);
      set({ lang });
    },
  };
});

/** `t('nav.dashboard')` — returns the Urdu string when Urdu is active, else
 * the key's English fallback if no translation is entered, so an untranslated
 * key never renders as a raw dictionary key. */
export function useT(): (key: string, fallback: string) => string {
  const lang = useI18n((s) => s.lang);
  return (key, fallback) => (lang === 'ur' ? DICTIONARY[key] ?? fallback : fallback);
}
